// ============================================
// Portal Auth Guard
// UKA-X01 D2 — Expert Clinical Review Portal
//
// Protects portal routes from unauthenticated access.
// - Unauthenticated → redirect to /portal/login
// - Session expired (>24h) → clear session + redirect to /portal/login
// - Authorized → render children
//
// Uses React.createElement (no JSX) to stay in .ts extension.
// ============================================

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  isAuthenticated,
  isSessionExpired,
  signOut,
  ensureFirebaseAuth,
} from './auth';

// ── Types ──

interface PortalAuthGuardProps {
  children: React.ReactNode;
  requiredRole?: string;
  unauthorizedRedirectTo?: string;
}

type GuardState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'session_expired' }
  | { status: 'authorized' };

// ── Guard Component ──

export function PortalAuthGuard({ children }: PortalAuthGuardProps) {
  const [state, setState] = useState<GuardState>({ status: 'loading' });

  useEffect(() => {
    async function check() {
      if (isSessionExpired()) {
        signOut();
        setState({ status: 'session_expired' });
        return;
      }

      if (isAuthenticated()) {
        await ensureFirebaseAuth();
        setState({ status: 'authorized' });
      } else {
        setState({ status: 'unauthenticated' });
      }
    }
    check();
  }, []);

  if (state.status === 'loading') {
    return React.createElement(GuardLoadingSpinner);
  }

  if (state.status === 'unauthenticated' || state.status === 'session_expired') {
    return React.createElement(Navigate, { to: '/portal/login', replace: true });
  }

  return React.createElement(React.Fragment, null, children);
}

// ── Loading Spinner ──

function GuardLoadingSpinner(): React.ReactElement {
  return React.createElement(
    'div',
    {
      style: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FAFAFA',
      },
    },
    React.createElement(
      'div',
      null,
      React.createElement(
        'div',
        {
          style: {
            width: '32px',
            height: '32px',
            border: '3px solid #F3F4F6',
            borderTopColor: '#E11D48',
            borderRadius: '50%',
            animation: 'portal-guard-spin 0.8s linear infinite',
          },
        },
      ),
      React.createElement(
        'style',
        null,
        '@keyframes portal-guard-spin { to { transform: rotate(360deg); } }',
      ),
    ),
  );
}

// ── Unauthorized Page ──

export function PortalUnauthorized(): React.ReactElement {
  return React.createElement(
    'div',
    {
      style: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FAFAFA',
        padding: '24px',
        fontFamily: 'DM Sans, system-ui, sans-serif',
      },
    },
    React.createElement(
      'div',
      {
        style: {
          backgroundColor: '#FFFFFF',
          borderRadius: '4px',
          padding: '40px 32px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
      React.createElement(
        'p',
        { style: { fontSize: '16px', color: '#18181B', margin: '0 0 8px', fontWeight: 600 } },
        'Access Restricted',
      ),
      React.createElement(
        'p',
        { style: { fontSize: '16px', color: '#52525B', margin: 0, lineHeight: '1.6' } },
        'Your account does not have permission to access this page.',
      ),
    ),
  );
}
